import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Play, Users, Calendar, Star, Clock, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { toast } from 'sonner';

export default function LessonsPage() {
  const [email, setEmail] = useState('');

  const handleNotify = (e) => {
    e.preventDefault();
    if (email) {
      toast.success('You\'ll be notified when lessons launch!');
      setEmail('');
    }
  };

  const upcomingCourses = [
    {
      title: "Batting Masterclass",
      instructor: "Pro Coach",
      level: "Beginner to Advanced",
      duration: "8 weeks",
      icon: "🏏"
    },
    {
      title: "Fast Bowling Techniques",
      instructor: "Pro Coach", 
      level: "Intermediate",
      duration: "6 weeks",
      icon: "🎯"
    },
    {
      title: "Spin Bowling Secrets",
      instructor: "Pro Coach",
      level: "All Levels",
      duration: "4 weeks",
      icon: "🌀"
    },
    {
      title: "Wicket Keeping Fundamentals",
      instructor: "Pro Coach",
      level: "Beginner",
      duration: "5 weeks",
      icon: "🧤"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1E4545] via-[#2D5A5A] to-[#3D6A6A]">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8">
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="bg-[#8B1E3F] text-white border-none mb-6 text-sm px-4 py-2">
            <Clock className="w-4 h-4 mr-2 inline" />
            Coming Soon
          </Badge>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Online Cricket<br />
            <span className="text-[#FFD700]">Lessons & Coaching</span>
          </h1>
          
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
            Learn from professional coaches with our upcoming online cricket academy. 
            Master batting, bowling, fielding, and game strategy from anywhere in the world.
          </p>

          {/* Notify Form */}
          <div className="max-w-md mx-auto mb-16">
            <form onSubmit={handleNotify} className="flex gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                required
              />
              <Button type="submit" className="h-12 px-6 bg-[#8B1E3F] hover:bg-[#6D1830]">
                <Bell className="w-4 h-4 mr-2" />
                Notify Me
              </Button>
            </form>
            <p className="text-white/60 text-sm mt-3">Be the first to know when we launch!</p>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <Play className="w-10 h-10 text-[#FFD700] mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">HD Video Lessons</h3>
              <p className="text-white/60 text-sm">Professional quality tutorials</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <Users className="w-10 h-10 text-[#FFD700] mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">Expert Coaches</h3>
              <p className="text-white/60 text-sm">Learn from the pros</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <BookOpen className="w-10 h-10 text-[#FFD700] mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">All Skill Levels</h3>
              <p className="text-white/60 text-sm">Beginner to advanced</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <Calendar className="w-10 h-10 text-[#FFD700] mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">Learn Anytime</h3>
              <p className="text-white/60 text-sm">Self-paced courses</p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Courses */}
      <section className="py-12 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Upcoming Courses
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {upcomingCourses.map((course, idx) => (
              <Card key={idx} className="bg-white/10 border-white/10 backdrop-blur-sm overflow-hidden">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">{course.icon}</div>
                  <h3 className="text-lg font-bold text-white mb-2">{course.title}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-white/60">
                      <span className="text-white/80">Instructor:</span> {course.instructor}
                    </p>
                    <p className="text-white/60">
                      <span className="text-white/80">Level:</span> {course.level}
                    </p>
                    <p className="text-white/60">
                      <span className="text-white/80">Duration:</span> {course.duration}
                    </p>
                  </div>
                  <Badge className="mt-4 bg-[#FFD700]/20 text-[#FFD700] border-none">
                    Coming Soon
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-[#8B1E3F] to-[#6D1830] rounded-3xl p-10">
            <Star className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Want to Partner With Us?
            </h2>
            <p className="text-white/80 mb-6">
              Are you a cricket coach or academy? Partner with CricORama to reach thousands of aspiring cricketers.
            </p>
            <Button variant="outline" className="border-white text-white hover:bg-white/10">
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center text-white/60 text-sm">
          © 2026 CricORama. Online Cricket Lessons coming soon.
        </div>
      </footer>
    </div>
  );
}
